import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ModuleKey, PARAM_ID } from '@cosmos/shared';
import { EntitlementService } from '@cosmos/angular/utils';
import { TranslatePipe } from '@ngx-translate/core';

export type ParamCounterDisplay = 'text' | 'bar';

const WARNING_THRESHOLD = 0.8;

/**
 * Self-contained display for a scalar tier param (e.g. max_saved_simulations,
 * simulation_duration_hours) — the entitlement system's non-metered bounds, as opposed to
 * MetricCounterComponent's bucketed usage counters.
 *
 * Reads the param's limit from EntitlementService.getParam (same moduleAccess signal as
 * MetricCounterComponent's buckets — no separate fetch, no staleness beyond the normal
 * refreshEntitlements() lifecycle).
 *
 * Renders:
 * - Nothing when the param isn't set for the current tier.
 * - "label: limit" when currentValue is not provided (informational — e.g. "Max duration
 *   hours: 168").
 * - "label: current / limit" when currentValue is provided — the caller owns tracking
 *   "current" (it isn't bucketed usage, so it can go up AND down, e.g. favourite/unfavourite).
 *
 * Translation keys for param labels follow the convention:
 *   metering.paramCounter.<PARAM_ID>
 */
@Component({
  selector: 'cosmos-param-counter',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    @if (visible()) {
      <div class="flex flex-col gap-1.5">

        <!-- Label row -->
        <div class="flex items-center gap-2 text-sm">
          <span class="text-surface-500">{{ labelKey() | translate }}:</span>
          @if (currentValue() === null) {
            <span class="font-bold">{{ limit() }}</span>
          } @else {
            <span [class]="valueClass()" class="font-bold">
              {{ currentValue() }}
              <span class="text-surface-500 font-bold"> / {{ limit() }}</span>
            </span>
          }
        </div>

        <!-- Progress bar (only when display === 'bar' and a current value is tracked) -->
        @if (display() === 'bar' && currentValue() !== null) {
          <div class="w-full h-1.5 rounded-full bg-surface-200 dark:bg-surface-700 overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-300"
              [class]="barClass()"
              [style.width.%]="fillPercent()">
            </div>
          </div>
        }

      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParamCounterComponent {
  private readonly entitlementService = inject(EntitlementService);

  readonly module = input.required<ModuleKey>();
  readonly param = input.required<PARAM_ID>();

  /**
   * Caller-owned "current usage" for this param (e.g. count of favourited simulations).
   * Omit for a purely informational display ("Max duration hours: 168"). Unlike
   * MetricCounterComponent's bucket remaining, this is not backend-metered — the caller is
   * expected to keep it in sync with whatever action changes it (both up and down).
   */
  readonly currentValue = input<number | null>(null);

  /** 'text' shows label + value only. 'bar' adds a progress bar below (only when currentValue is set). */
  readonly display = input<ParamCounterDisplay>('text');

  // ── Derived state ──────────────────────────────────────────────────────────

  readonly limit = computed(() =>
    this.entitlementService.getParam(this.module(), this.param())
  );

  readonly visible = computed(() => this.limit() !== null);

  readonly fillPercent = computed(() => {
    const limit = this.limit();
    const current = this.currentValue();
    if (!limit || current === null) return 0;
    return Math.min(Math.max(current, 0) / limit * 100, 100);
  });

  /** Color scale: red when at/over the cap, orange when close, neutral otherwise. */
  readonly valueClass = computed(() => {
    const fill = this.fillPercent();
    if (fill >= 100) return 'text-red-500 font-bold';
    if (fill >= WARNING_THRESHOLD * 100) return 'text-orange-400 font-bold';
    return 'text-surface-500 font-bold';
  });

  readonly barClass = computed(() => {
    const fill = this.fillPercent();
    if (fill >= 100) return 'bg-red-500';
    if (fill >= WARNING_THRESHOLD * 100) return 'bg-orange-400';
    return 'bg-green-600';
  });

  readonly labelKey = computed(() => `metering.paramCounter.${this.param()}`);
}
